// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.
import { BaseItem } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { QuickTableColumnType, QuickTableEditingInfo } from '../elements/QuickTable/QuickTableMisc';
import { DataCache } from '../../Commons/DataCache';
import { RulesSet } from '../../Utils/Rules/RulesSet';
import { RiskPlan } from '../../Commons/cache/RiskPlan';
import { type SavedOrder } from '../../Commons/cache/SavedOrders/SavedOrder';
import { type SessionSettings } from '../../Commons/SessionSettings';
import { DynProperty } from '../../Commons/DynProperty';
import { MathUtils } from '../../Utils/MathUtils';
import { OperationType } from '../../Utils/Trading/OperationType';
import { Resources } from '../../Commons/properties/Resources';
import { OrderTypeBase } from '../../Commons/cache/OrderParams/order-type/OrderTypeBase';
import { OrderUtils } from '../../Utils/Trading/OrderUtils';
import { TIF, OrderTifMap } from '../../Utils/Trading/OrderTif';
import { NumericUtils } from '../../Utils/NumericUtils';
import { Intervals } from '../../Utils/Instruments/Intervals';
import { OrderType } from '../../Utils/Trading/OrderType';
import { Quantity } from '../../Utils/Trading/Quantity';
import { GeneralSettings } from '../../Utils/GeneralSettings/GeneralSettings';
import { OffsetModeViewEnum } from '../../Utils/Trading/OffsetModeViewEnum';
import { type Account } from '../../Commons/cache/Account';
import { type Instrument } from '../../Commons/cache/Instrument';
import { InstrumentUtils } from '../../Utils/Instruments/InstrumentUtils';
import { ProductType } from '../../Utils/Instruments/ProductType';
import { LinkedPriceType } from '../../Commons/cache/SavedOrders/SavedOrderEnums';
import EventEmitter from 'events';

export class SavedOrderItem extends BaseItem {
    private readonly _eventEmitter: EventEmitter = new EventEmitter();
    public readonly savedOrder: SavedOrder;
    public ItemId: number;
    public QuickTableEditingInfoMap: object;

    constructor (savedOrder: SavedOrder, sessionSettings: typeof SessionSettings) {
        super(sessionSettings);
        this.savedOrder = savedOrder;

        if (!MathUtils.IsNullOrUndefined(this.savedOrder)) {
            this.QuickTableEditingInfoMap[SavedOrderItem.ACTIVE_COL_INDEX] = new QuickTableEditingInfo(DynProperty.BOOLEAN);
            const accountLookup = new QuickTableEditingInfo(DynProperty.ACCOUNT);
            accountLookup.GetDataHandler = this.GetCurrentAccount.bind(this);
            this.QuickTableEditingInfoMap[SavedOrderItem.ACCOUNT_COL_INDEX] = accountLookup;

            const instrumentLookup = new QuickTableEditingInfo(DynProperty.INSTRUMENT);
            instrumentLookup.GetDataHandler = this.GetCurrentInstrument.bind(this);
            this.QuickTableEditingInfoMap[SavedOrderItem.INSTRUMENT_COL_INDEX] = instrumentLookup;

            const operationCombobox = new QuickTableEditingInfo(DynProperty.COMBOBOX);
            operationCombobox.GetDataHandler = this.getOpeationComboboxItems;
            this.QuickTableEditingInfoMap[SavedOrderItem.OPERATION_COL_INDEX] = operationCombobox;

            const orderTypeCombobox = new QuickTableEditingInfo(DynProperty.COMBOBOX);
            orderTypeCombobox.GetDataHandler = this.getOrderTypeComboboxItems;
            this.QuickTableEditingInfoMap[SavedOrderItem.ORDER_TYPE_COL_INDEX] = orderTypeCombobox;

            const tifCombobox = new QuickTableEditingInfo(DynProperty.COMBOBOX_COMBOITEM_TIF);
            tifCombobox.GetDataHandler = this.getTifComboboxItems;
            this.QuickTableEditingInfoMap[SavedOrderItem.TIF_COL_INDEX] = tifCombobox;

            const productTypeCombobox = new QuickTableEditingInfo(DynProperty.COMBOBOX);
            productTypeCombobox.GetDataHandler = this.getProductTypeComboboxItems;
            this.QuickTableEditingInfoMap[SavedOrderItem.PRODUCT_TYPE_COL_INDEX] = productTypeCombobox;

            const linkedPriceTypeCombobox = new QuickTableEditingInfo(DynProperty.COMBOBOX);
            linkedPriceTypeCombobox.GetDataHandler = this.getLinkedPriceTypeComboboxItems;
            this.QuickTableEditingInfoMap[SavedOrderItem.LINK_COL_INDEX] = linkedPriceTypeCombobox;

            this.QuickTableEditingInfoMap[SavedOrderItem.QUANTITY_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);
            this.QuickTableEditingInfoMap[SavedOrderItem.DISCLOSED_QUANTITY_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);

            this.QuickTableEditingInfoMap[SavedOrderItem.PRICE_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);
            this.QuickTableEditingInfoMap[SavedOrderItem.STOP_PRICE_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);
            this.QuickTableEditingInfoMap[SavedOrderItem.SL_PRICE_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);
            this.QuickTableEditingInfoMap[SavedOrderItem.SLL_PRICE_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);
            this.QuickTableEditingInfoMap[SavedOrderItem.TP_PRICE_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);

            this.QuickTableEditingInfoMap[SavedOrderItem.LINKED_PRICE_OFFSET_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);
            this.QuickTableEditingInfoMap[SavedOrderItem.TRAILING_STOP_COL_INDEX] = new QuickTableEditingInfo(DynProperty.BOOLEAN);
            this.QuickTableEditingInfoMap[SavedOrderItem.SL_OFFSET_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);
            this.QuickTableEditingInfoMap[SavedOrderItem.SLL_OFFSET_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);
            this.QuickTableEditingInfoMap[SavedOrderItem.TP_OFFSET_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);

            const leverageCombobox = new QuickTableEditingInfo(DynProperty.COMBOBOX);
            leverageCombobox.GetDataHandler = this.getLeverageComboboxItems;
            this.QuickTableEditingInfoMap[SavedOrderItem.LEVERAGE_COL_INDEX] = leverageCombobox;

            this.QuickTableEditingInfoMap[SavedOrderItem.REMOVE_ORDER_COL_INDEX] = new QuickTableEditingInfo(DynProperty.CLOSE_BUTTON);
            this.QuickTableEditingInfoMap[SavedOrderItem.SUBMIT_COL_INDEX] = new QuickTableEditingInfo(DynProperty.PLACE_BUTTON);

            this.updateNumerics();

            this.savedOrder.SubscribeOnUpdate(this.onUpdateSavedOrder);
            this.savedOrder.SubscribeOnUpdateInstrument(this.onUpdateSavedOrderInstrument);
            this.savedOrder.SubscribeOnUpdateDefaultPrices(this.onUpdateSavedOrderDefaultPrices);
        }
    }

    private readonly onUpdateSavedOrder = (): void => {
        super.fireUpdate();
        this._eventEmitter.emit('update', this);
    };

    private readonly onUpdateSavedOrderInstrument = (): void => {
        this.updateNumerics();
    };

    private readonly onUpdateSavedOrderDefaultPrices = (): void => {
        this.updateNumerics();
    };

    private readonly getOpeationComboboxItems = (): Array<{ text: string, value: number }> => {
        const operationComboboxItems: Array<{ text: string, value: number }> = [];
        operationComboboxItems.push({ text: Resources.getResource('general.trading.Buy'), value: OperationType.Buy });
        operationComboboxItems.push({ text: Resources.getResource('general.trading.Sell'), value: OperationType.Sell });
        return operationComboboxItems;
    };

    private readonly getOrderTypeComboboxItems = (): Array<{ text: string, value: number }> => {
        const orderTypesComboboxItems: Array<{ text: string, value: number }> = [];
        const orderTypes: object = DataCache.OrderParameterContainer.OrderTypes;
        const supportedParamObj = new OrderTypeBase.Parameters().setAccount(this.savedOrder.Account).setInstrument(this.savedOrder.Instrument);
        for (const key in orderTypes) {
            const orderType: OrderTypeBase = orderTypes[key];
            const isSupportedOrderType: boolean = orderType.IsSupported(supportedParamObj);
            if (!isSupportedOrderType) {
                continue;
            };
            orderTypesComboboxItems.push({ text: Resources.getResource(`property.${OrderUtils.getOrderTypeLocalizationKey(orderType.id())}`), value: orderType.id() });
        }
        return orderTypesComboboxItems;
    };

    private readonly getTifComboboxItems = (): { items: Array<{ text: string, value: number }>, selectedItem: any } => {
        const tifComboboxItems: Array<{ text: string, value: number }> = [];
        const selectedTif = new TIF(this.savedOrder.TIF, this.savedOrder.ExpirityDateStr);

        if (MathUtils.IsNullOrUndefined(this.savedOrder.Instrument) || MathUtils.IsNullOrUndefined(this.savedOrder.OrderType)) {
            return { items: tifComboboxItems, selectedItem: selectedTif };
        }

        if (DataCache.OrderParameterContainer === null) {
            return { items: tifComboboxItems, selectedItem: selectedTif };
        }

        const supportedParamObj = new OrderTypeBase.Parameters().setInstrument(this.savedOrder.Instrument);
        const currentOrderType = DataCache.OrderParameterContainer.GetOrderType(this.savedOrder.OrderType);
        if (MathUtils.IsNullOrUndefined(currentOrderType)) {
            return { items: tifComboboxItems, selectedItem: selectedTif };
        };
        const isSupportedOrderType: boolean = currentOrderType.IsSupported(supportedParamObj);
        if (!isSupportedOrderType) {
            return { items: tifComboboxItems, selectedItem: selectedTif };
        }

        const route = DataCache.getRouteByName(this.savedOrder.Instrument.Route);
        const tifs = route.DictOrderTypeTifs[this.savedOrder.OrderType];
        if (MathUtils.IsNullOrUndefined(tifs)) {
            return { items: tifComboboxItems, selectedItem: selectedTif };
        }

        for (let i = 0; i < tifs.length; i++) {
            tifComboboxItems.push({ text: Resources.getResource(OrderTifMap[tifs[i]]), value: tifs[i] });
        }

        return { items: tifComboboxItems, selectedItem: selectedTif };
    };

    private readonly getProductTypeComboboxItems = (): Array<{ text: string, value: number }> => {
        const account: Account = this.savedOrder.Account;
        const instrument: Instrument = this.savedOrder.Instrument;
        const productTypesComboboxItems: Array<{ text: string, value: number }> = [];

        if (MathUtils.IsNullOrUndefined(account) || MathUtils.IsNullOrUndefined(instrument)) {
            return productTypesComboboxItems;
        }

        const dict = !MathUtils.IsNullOrUndefined(account.RiskPlan) ? account.RiskPlan.GetRisksForInstrument(instrument.GetInteriorID()).availableProductTypes : InstrumentUtils.getAllowedProductTypeDict(instrument);
        if (dict.length === 0) {
            return productTypesComboboxItems;
        }
        if (dict.length === 1 && Number(dict[0]) === ProductType.General) {
            return productTypesComboboxItems;
        }

        const productTypes: number[] = dict.map((productType: string) => { return Number(productType); });
        for (const productType of productTypes) {
            productTypesComboboxItems.push({ text: InstrumentUtils.GetLocalizedProductType(instrument, productType), value: productType });
        }
        return productTypesComboboxItems;
    };

    private readonly getLinkedPriceTypeComboboxItems = (): Array<{ text: string, value: number }> => {
        const linkedPriceTypeComboboxItems: Array<{ text: string, value: number }> = [];
        linkedPriceTypeComboboxItems.push({ text: SavedOrderItem.LocalizeLinkPriceType(LinkedPriceType.None), value: LinkedPriceType.None });
        linkedPriceTypeComboboxItems.push({ text: SavedOrderItem.LocalizeLinkPriceType(LinkedPriceType.Bid), value: LinkedPriceType.Bid });
        linkedPriceTypeComboboxItems.push({ text: SavedOrderItem.LocalizeLinkPriceType(LinkedPriceType.Ask), value: LinkedPriceType.Ask });
        linkedPriceTypeComboboxItems.push({ text: SavedOrderItem.LocalizeLinkPriceType(LinkedPriceType.Last), value: LinkedPriceType.Last });
        return linkedPriceTypeComboboxItems;
    };

    private readonly getLeverageComboboxItems = (): Array<{ text: string, value: number }> => {
        const account: Account = this.savedOrder.Account;
        const instrument: Instrument = this.savedOrder.Instrument;
        const productType: ProductType = this.savedOrder.ProductType;
        const leverageComboboxItems: Array<{ text: string, value: number }> = [];

        if (MathUtils.IsNullOrUndefined(account) || MathUtils.IsNullOrUndefined(instrument)) {
            return leverageComboboxItems;
        }

        const isLeverageVisible: boolean = instrument.isLeverageVisible(account, productType);
        if (isLeverageVisible) {
            const leverages: number[] = instrument.getLeverages(account, productType);
            for (const leverage of leverages) {
                leverageComboboxItems.push({ text: leverage.toString(), value: leverage });
            }
        }

        return leverageComboboxItems;
    };

    private updatePriceNumeric (index: number): void {
        const instrument = this.savedOrder.Instrument;
        let min: number = 0;
        let decimalPlaces: number = 0;
        let increment: number = 1;
        let intervals: any = null;
        if (!MathUtils.IsNullOrUndefined(instrument)) {
            const numericLimits = NumericUtils.getNumericsOffsetModeViewParams(instrument, false, false);
            intervals = Intervals.GetIntervalsFromInstrument(instrument, NumericUtils.MAXVALUE);
            min = numericLimits.step;
            decimalPlaces = numericLimits.numericsPrec;
            increment = numericLimits.step;
        }
        const priceNumeric = this.QuickTableEditingInfoMap[index];
        priceNumeric.Min = min;
        priceNumeric.Max = NumericUtils.MAXVALUE;
        priceNumeric.Inc = increment;
        priceNumeric.DecimalPlaces = decimalPlaces;
        priceNumeric.IntervalsListForNumeric = intervals;
    }

    private updateOffsetNumeric (index: number, isAllowNegative: boolean = false, isTrailingStop: boolean = false): void {
        const instrument = this.savedOrder.Instrument;
        let min: number = isAllowNegative ? -NumericUtils.MAXVALUE : 1;
        let decimalPlaces: number = 0;
        let increment: number = 1;
        if (!MathUtils.IsNullOrUndefined(instrument)) {
            const numericLimits = NumericUtils.getNumericsOffsetModeViewParams(instrument, true, isTrailingStop);
            if (!isAllowNegative) {
                min = numericLimits.step;
            }
            decimalPlaces = numericLimits.numericsPrec;
            increment = numericLimits.step;
        }
        const priceNumeric = this.QuickTableEditingInfoMap[index];
        priceNumeric.Min = min;
        priceNumeric.Max = NumericUtils.MAXVALUE;
        priceNumeric.Inc = increment;
        priceNumeric.DecimalPlaces = decimalPlaces;
    }

    private updateAmountNumeric (index: number): void {
        const account = this.savedOrder.Account;
        const instrument = this.savedOrder.Instrument;
        const productType = this.savedOrder.ProductType;
        let min: number = 1;
        let max: number = NumericUtils.MAXVALUE;
        let increment: number = 1;
        let decimalPlaces: number = 0;

        if (!MathUtils.IsNullOrUndefined(instrument)) {
            min = Math.max(instrument.getMinLot(productType, account), instrument?.LotStep);
            max = instrument?.getMaxLot(productType, account);
            increment = instrument.LotStep;
            decimalPlaces = instrument.getLotStepPrecision();
            if (!GeneralSettings.View.displayAmountInLots()) {
                min *= instrument.LotSize;
                max *= instrument.LotSize;
                increment *= instrument.LotSize;
                decimalPlaces = 0;
            }
        }

        const amountNumeric = this.QuickTableEditingInfoMap[index];
        amountNumeric.Min = min;
        amountNumeric.Max = max;
        amountNumeric.Inc = increment;
        amountNumeric.DecimalPlaces = decimalPlaces;
    }

    public updateNumerics (): void {
        this.updatePriceNumeric(SavedOrderItem.PRICE_COL_INDEX);
        if (this.savedOrder.OrderType === OrderType.TrailingStop) {
            this.updateOffsetNumeric(SavedOrderItem.STOP_PRICE_COL_INDEX, false, true);
        } else {
            this.updatePriceNumeric(SavedOrderItem.STOP_PRICE_COL_INDEX);
        }
        this.updatePriceNumeric(SavedOrderItem.SL_PRICE_COL_INDEX);
        this.updatePriceNumeric(SavedOrderItem.SLL_PRICE_COL_INDEX);
        this.updatePriceNumeric(SavedOrderItem.TP_PRICE_COL_INDEX);

        this.updateOffsetNumeric(SavedOrderItem.LINKED_PRICE_OFFSET_COL_INDEX, true);
        this.updateOffsetNumeric(SavedOrderItem.SL_OFFSET_COL_INDEX);
        this.updateOffsetNumeric(SavedOrderItem.SLL_OFFSET_COL_INDEX, true);
        this.updateOffsetNumeric(SavedOrderItem.TP_OFFSET_COL_INDEX);

        this.updateAmountNumeric(SavedOrderItem.QUANTITY_COL_INDEX);
        this.updateAmountNumeric(SavedOrderItem.DISCLOSED_QUANTITY_COL_INDEX);
    }

    public Dispose (): void {
        if (!MathUtils.IsNullOrUndefined(this.savedOrder)) {
            this.savedOrder.UnubscribeOnUpdate(this.onUpdateSavedOrder);
            this.savedOrder.UnsubscribeOnUpdateInstrument(this.onUpdateSavedOrderInstrument);
        }
        super.Dispose();
    }

    public GetCurrentAccount (): any { return this.savedOrder?.Account; }
    public GetCurrentInstrument (): any { return this.savedOrder?.Instrument; }

    public ColumnCount (): number {
        return SavedOrderItem.columnsParams.length;
    }

    public GetColumnParams (index: number): any {
        const columnParams = SavedOrderItem.columnsParams[index];
        switch (index) {
        case SavedOrderItem.TRAILING_STOP_COL_INDEX:
            columnParams.Hidden = !DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRAILING_STOP);
            break;
        case SavedOrderItem.ROUTE_COL_INDEX:
            columnParams.Hidden = !DataCache.isAllowedForMyUser(RulesSet.FUNCTION_POSITIONSADMIN);
            break;
        case SavedOrderItem.SL_PRICE_COL_INDEX:
        case SavedOrderItem.TP_PRICE_COL_INDEX:
        case SavedOrderItem.SL_OFFSET_COL_INDEX:
        case SavedOrderItem.TP_OFFSET_COL_INDEX:
        case SavedOrderItem.SLL_PRICE_COL_INDEX:
        case SavedOrderItem.SLL_OFFSET_COL_INDEX:
            columnParams.Hidden = !DataCache.isAllowedForMyUser(RulesSet.FUNCTION_SLTP);
            break;
        case SavedOrderItem.PRODUCT_TYPE_COL_INDEX:
            columnParams.Hidden = !RiskPlan.showProductType;
            break;
        }
        return columnParams;
    }

    public getColumnData (index: number): { Value: any, FormattedValue: any } {
        const value = this.getColumnValue(index);
        let formattedValue: string = '';
        switch (index) {
        case SavedOrderItem.ACCOUNT_COL_INDEX:
            formattedValue = this.savedOrder.AccountStr;
            break;
        case SavedOrderItem.OPERATION_COL_INDEX:
            formattedValue = this.savedOrder.OperationStr;
            break;
        case SavedOrderItem.ORDER_TYPE_COL_INDEX:
            formattedValue = this.savedOrder.OrderTypeStr;
            break;
        case SavedOrderItem.QUANTITY_COL_INDEX:
        case SavedOrderItem.DISCLOSED_QUANTITY_COL_INDEX:
            formattedValue = this.savedOrder.formatAmount(value);
            break;
        case SavedOrderItem.TIF_COL_INDEX:
            formattedValue = this.savedOrder.TifStr;
            break;
        case SavedOrderItem.STATUS_COL_INDEX:
            formattedValue = this.savedOrder.StatusStr;
            break;
        case SavedOrderItem.LINK_COL_INDEX:
            formattedValue = SavedOrderItem.LocalizeLinkPriceType(this.savedOrder.LinkedPriceType);
            break;
        case SavedOrderItem.LINKED_PRICE_OFFSET_COL_INDEX:
            formattedValue = this.savedOrder.formatOffset(this.savedOrder.LinkedPriceOffset);
            break;
        case SavedOrderItem.PRICE_COL_INDEX:
        case SavedOrderItem.BID_COL_INDEX:
        case SavedOrderItem.ASK_COL_INDEX:
        case SavedOrderItem.LAST_COL_INDEX:
        case SavedOrderItem.SL_PRICE_COL_INDEX:
        case SavedOrderItem.SLL_PRICE_COL_INDEX:
        case SavedOrderItem.TP_PRICE_COL_INDEX:
            formattedValue = this.savedOrder.formatPrice(value);
            break;
        case SavedOrderItem.STOP_PRICE_COL_INDEX:
            if (this.savedOrder.OrderType === OrderType.TrailingStop) {
                formattedValue = `${this.savedOrder.formatOffset(this.savedOrder.StopPrice)} (${Resources.getResource('panel.savedOrders.Trailing')})`;
            } else {
                formattedValue = this.savedOrder.formatPrice(this.savedOrder.StopPrice);
            }
            break;
        case SavedOrderItem.SL_OFFSET_COL_INDEX:
            formattedValue = this.savedOrder.formatOffset(this.savedOrder.SlOffset);
            break;
        case SavedOrderItem.SLL_OFFSET_COL_INDEX:
            formattedValue = this.savedOrder.formatOffset(this.savedOrder.SllOffset);
            break;
        case SavedOrderItem.TP_OFFSET_COL_INDEX:
            formattedValue = this.savedOrder.formatOffset(this.savedOrder.TpOffset);
            break;
        case SavedOrderItem.PRODUCT_TYPE_COL_INDEX:
            formattedValue = this.savedOrder.ProductTypeStr;
            break;
        case SavedOrderItem.SYMBOL_TYPE_COL_INDEX:
            formattedValue = this.savedOrder.SymbolTypeStr;
            break;
        case SavedOrderItem.LEVERAGE_COL_INDEX:
            formattedValue = this.savedOrder.LegerageStr;
            break;

        default:
            formattedValue = value.toString();
            break;
        }

        return {
            Value: value,
            FormattedValue: formattedValue
        };
    }

    public getColumnValue (index: number): any {
        switch (index) {
        case SavedOrderItem.ACTIVE_COL_INDEX:
            return this.savedOrder.Active;
        case SavedOrderItem.ACCOUNT_COL_INDEX:
            return this.savedOrder.AccountStr;
        case SavedOrderItem.INSTRUMENT_COL_INDEX:
            return this.savedOrder.InstrumentStr;
        case SavedOrderItem.OPERATION_COL_INDEX:
            return this.savedOrder.Operation;
        case SavedOrderItem.ORDER_TYPE_COL_INDEX:
            return this.savedOrder.OrderType;
        case SavedOrderItem.PRICE_COL_INDEX:
            return this.savedOrder.Price;
        case SavedOrderItem.BID_COL_INDEX:
            return this.savedOrder.Bid;
        case SavedOrderItem.ASK_COL_INDEX:
            return this.savedOrder.Ask;
        case SavedOrderItem.LAST_COL_INDEX:
            return this.savedOrder.Last;
        case SavedOrderItem.QUANTITY_COL_INDEX:
            return Quantity.convertLotsToAmount(this.savedOrder.QuantityLots, this.savedOrder.Instrument);
        case SavedOrderItem.DISCLOSED_QUANTITY_COL_INDEX:
            return Quantity.convertLotsToAmount(this.savedOrder.DisclosedQuantityLots, this.savedOrder.Instrument);
        case SavedOrderItem.TIF_COL_INDEX:
            return this.savedOrder.TIF;
        case SavedOrderItem.STATUS_COL_INDEX:
            return this.savedOrder.Status;
        case SavedOrderItem.TRAILING_STOP_COL_INDEX:
            return this.savedOrder.SLTPHolder.isTrailingStop();
        case SavedOrderItem.STOP_PRICE_COL_INDEX:
            if (this.savedOrder.OrderType === OrderType.TrailingStop) {
                return OrderUtils.ConvertTickOffset(this.savedOrder.Instrument,
                    GeneralSettings.TradingDefaults.ShowOffsetIn,
                    this.savedOrder.Instrument.GetMarketPrice(this.savedOrder.Account, this.savedOrder.Operation),
                    this.savedOrder.StopPrice);
            } else {
                return this.savedOrder.StopPrice;
            }
        case SavedOrderItem.OPTIONS_TYPE_COL_INDEX:
            return this.savedOrder.OptionsTypeStr;
        case SavedOrderItem.STRIKE_PRICE_COL_INDEX:
            return this.savedOrder.StrikePriceStr;
        case SavedOrderItem.EXPIRY_DATE_COL_INDEX:
            return this.savedOrder.ExpirityDateStr;
        case SavedOrderItem.LINK_COL_INDEX:
            return this.savedOrder.LinkedPriceType;
        case SavedOrderItem.LINKED_PRICE_OFFSET_COL_INDEX:
            return OrderUtils.ConvertTickOffset(this.savedOrder.Instrument,
                GeneralSettings.TradingDefaults.ShowOffsetIn,
                this.savedOrder.Instrument.GetMarketPrice(this.savedOrder.Account, this.savedOrder.Operation),
                this.savedOrder.LinkedPriceOffset,
                true);
        case SavedOrderItem.TRADING_EXCHANGE_COL_INDEX:
            return this.savedOrder.TradingExchange;
        case SavedOrderItem.ROUTE_COL_INDEX:
            return this.savedOrder.Route;
        case SavedOrderItem.SL_PRICE_COL_INDEX:
            return this.savedOrder.SlPrice;
        case SavedOrderItem.SLL_PRICE_COL_INDEX:
            return this.savedOrder.SllPrice;
        case SavedOrderItem.TP_PRICE_COL_INDEX:
            return this.savedOrder.TpPrice;
        case SavedOrderItem.SL_OFFSET_COL_INDEX:
            return OrderUtils.ConvertTickOffset(this.savedOrder.Instrument,
                this.savedOrder.SLTPHolder.isTrailingStop() && !GeneralSettings.TradingDefaults.SetSlTpValuesInOffset ? OffsetModeViewEnum.Ticks : GeneralSettings.TradingDefaults.ShowOffsetIn,
                this.savedOrder.Instrument.GetMarketPrice(this.savedOrder.Account, this.savedOrder.Operation),
                this.savedOrder.SlOffset);
        case SavedOrderItem.SLL_OFFSET_COL_INDEX:
            return OrderUtils.ConvertTickOffset(this.savedOrder.Instrument,
                this.savedOrder.SLTPHolder.isTrailingStop() && !GeneralSettings.TradingDefaults.SetSlTpValuesInOffset ? OffsetModeViewEnum.Ticks : GeneralSettings.TradingDefaults.ShowOffsetIn,
                this.savedOrder.Instrument.GetMarketPrice(this.savedOrder.Account, this.savedOrder.Operation),
                this.savedOrder.SllOffset,
                true);
        case SavedOrderItem.TP_OFFSET_COL_INDEX:
            return OrderUtils.ConvertTickOffset(this.savedOrder.Instrument,
                GeneralSettings.TradingDefaults.ShowOffsetIn,
                this.savedOrder.Instrument.GetMarketPrice(this.savedOrder.Account, this.savedOrder.Operation),
                this.savedOrder.TpOffset);
        case SavedOrderItem.PRODUCT_TYPE_COL_INDEX:
            return this.savedOrder.ProductType;
        case SavedOrderItem.SYMBOL_TYPE_COL_INDEX:
            return this.savedOrder.SymbolType;
        case SavedOrderItem.LEVERAGE_COL_INDEX:
            return this.savedOrder.Leverage;
        default:
            return '';
        }
    }

    public subsribeOnUpdate (callback: (item: SavedOrderItem) => void): void {
        this._eventEmitter.on('update', callback);
    }

    public unsubsribeOnUpdate (callback: (item: SavedOrderItem) => void): void {
        this._eventEmitter.off('update', callback);
    }

    static readonly ACTIVE_COL_INDEX = 0;
    static readonly ACCOUNT_COL_INDEX = 1;
    static readonly INSTRUMENT_COL_INDEX = 2;
    static readonly ROUTE_COL_INDEX = 3;
    static readonly SYMBOL_TYPE_COL_INDEX = 4;
    static readonly OPERATION_COL_INDEX = 5;
    static readonly ORDER_TYPE_COL_INDEX = 6;
    static readonly PRODUCT_TYPE_COL_INDEX = 7;
    static readonly PRICE_COL_INDEX = 8;
    static readonly QUANTITY_COL_INDEX = 9;
    static readonly DISCLOSED_QUANTITY_COL_INDEX = 10;
    static readonly STOP_PRICE_COL_INDEX = 11;
    static readonly TIF_COL_INDEX = 12;
    static readonly LINKED_PRICE_OFFSET_COL_INDEX = 13;
    static readonly TRAILING_STOP_COL_INDEX = 14;
    static readonly OPTIONS_TYPE_COL_INDEX = 15;
    static readonly STRIKE_PRICE_COL_INDEX = 16;
    static readonly EXPIRY_DATE_COL_INDEX = 17;
    static readonly LAST_COL_INDEX = 18;
    static readonly BID_COL_INDEX = 19;
    static readonly ASK_COL_INDEX = 20;
    static readonly STATUS_COL_INDEX = 21; // TODO
    static readonly LINK_COL_INDEX = 22;
    static readonly SL_PRICE_COL_INDEX = 23;
    static readonly SLL_PRICE_COL_INDEX = 24;
    static readonly TP_PRICE_COL_INDEX = 25;
    static readonly SL_OFFSET_COL_INDEX = 26;
    static readonly SLL_OFFSET_COL_INDEX = 27;
    static readonly TP_OFFSET_COL_INDEX = 28;
    static readonly TRADING_EXCHANGE_COL_INDEX = 29;
    static readonly LEVERAGE_COL_INDEX = 30;
    static readonly REMOVE_ORDER_COL_INDEX = 31;
    static readonly SUBMIT_COL_INDEX = 32;

    static columnsParams: ColumnParams[] =
        [
            new ColumnParams('panel.savedOrders.active', 70, QuickTableColumnType.COL_DEFAULT, false, true, false),
            new ColumnParams('panel.savedOrders.account', 200, QuickTableColumnType.COL_DEFAULT, false, false, false, false, true),
            new ColumnParams('panel.savedOrders.instrument', 200, QuickTableColumnType.COL_DEFAULT, false, true, false, false, true),
            new ColumnParams('panel.savedOrders.route', 100, QuickTableColumnType.COL_DEFAULT, false, false, true),
            new ColumnParams('panel.savedOrders.SymbolType', 65, QuickTableColumnType.COL_DEFAULT, false, false, true),
            new ColumnParams('panel.savedOrders.operation', 100, QuickTableColumnType.COL_DEFAULT, false, true, false, false, true),
            new ColumnParams('panel.savedOrders.OrderType', 100, QuickTableColumnType.COL_DEFAULT, false, true, false),
            new ColumnParams('panel.savedOrders.ProductType', 100, QuickTableColumnType.COL_DEFAULT, false, false, false),
            new ColumnParams('panel.savedOrders.price', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
            new ColumnParams('panel.savedOrders.quantity', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, true, true, false),
            new ColumnParams('panel.savedOrders.disclosedQty', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, true, false, false),
            new ColumnParams('panel.savedOrders.stopPrice', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
            new ColumnParams('panel.savedOrders.tif', 100, QuickTableColumnType.COL_DEFAULT, false, true, false),
            new ColumnParams('panel.savedOrders.LinkedPriceOffset', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
            new ColumnParams('panel.savedOrders.TrailingStop', 70, QuickTableColumnType.COL_DEFAULT, false, true, false),
            new ColumnParams('panel.savedOrders.OptionsType', 100, QuickTableColumnType.COL_DEFAULT, false, false, false),
            new ColumnParams('panel.savedOrders.StrikePrice', 100, QuickTableColumnType.COL_DEFAULT, false, false, false),
            new ColumnParams('panel.savedOrders.ExpiryDate', 100, QuickTableColumnType.COL_DEFAULT, false, false, false),
            new ColumnParams('panel.savedOrders.last', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
            new ColumnParams('panel.savedOrders.bid', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
            new ColumnParams('panel.savedOrders.ask', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
            new ColumnParams('panel.savedOrders.status', 200, QuickTableColumnType.COL_DEFAULT, false, true, false),
            new ColumnParams('panel.savedOrders.Link', 100, QuickTableColumnType.COL_DEFAULT, false, true, false),
            new ColumnParams('panel.savedOrders.SL_Price', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
            new ColumnParams('panel.savedOrders.SLL_Price', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
            new ColumnParams('panel.savedOrders.TP_Price', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
            new ColumnParams('panel.savedOrders.SL_Offset', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
            new ColumnParams('panel.savedOrders.SLL_Offset', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
            new ColumnParams('panel.savedOrders.TP_Offset', 100, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
            new ColumnParams('panel.savedOrders.ExchangeTrading', 100, QuickTableColumnType.COL_DEFAULT, false, false, false),
            new ColumnParams('panel.savedOrders.Leverage', 100, QuickTableColumnType.COL_DATE_SORT, false, false, true),
            new ColumnParams('panel.savedOrders.RemoveOrder', 70, QuickTableColumnType.COL_DEFAULT, false, true, false),
            new ColumnParams('panel.savedOrders.SubmitOrder', 70, QuickTableColumnType.COL_DEFAULT, false, true, false)
        ];

    static LocalizeLinkPriceType (type: LinkedPriceType): string {
        switch (type) {
        case LinkedPriceType.None:
            return Resources.getResource('property.None');
        case LinkedPriceType.Bid:
            return Resources.getResource('property.Bid');
        case LinkedPriceType.Ask:
            return Resources.getResource('property.Ask');
        case LinkedPriceType.Last:
            return Resources.getResource('property.Last');
        default:
            return '';
        }
    }
}
